import { ButtonBase } from '@mui/material';
import SemanticGradeDisplay from 'components/InstructorSubmissions/SemanticGradeDisplay';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Icon, Menu, MenuItem, Select, Stack, styled, Text, Tooltip } from 'shell/components';

const STATUS_PRESENTATION = {
    not_started: {
        icon: 'clock',
        translationKey: 'status.ready-for-review',
        fallback: 'Ready for Review',
    },
    in_progress: {
        icon: 'edit',
        translationKey: 'status.partially-reviewed',
        fallback: 'Partially Reviewed',
    },
    confirmed: {
        icon: 'checkmark',
        translationKey: 'status.reviewed',
        fallback: 'Reviewed',
    },
};

const SubmissionNavigationDropdown = ({
    disabled,
    currentSubmission,
    options,
    sectionOptions,
    sectionId,
    totalSubmissions,
    isGroupAssignment,
    sort,
    setSort,
    selectSubmission,
    selectSection,
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const { t } = useTranslation();

    const id = 'grading-select-submission-dropdown';
    const open = Boolean(anchorEl);
    const isUnscored = options.some((option) => option.isUnscored);
    const currentOptionIndex = options.findIndex(({ key }) => key === currentSubmission.id);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleSelectSubmission = (event, submissionId) => {
        event.stopPropagation();
        handleClose();
        if (submissionId !== currentSubmission.id) selectSubmission(submissionId);
    };

    return (
        <Box>
            <ProgressButton
                id={id}
                aria-controls={open ? `${id}-menu` : undefined}
                aria-expanded={open ? true : undefined}
                aria-haspopup={'menu'}
                disabled={disabled}
                onClick={handleClick}
            >
                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                    <Text color={'inherit'} variant={'p2'} weight={'semibold'}>
                        {t('ui.progress', 'Progress')}
                    </Text>
                    <Stack alignItems={'center'} direction={'row'} spacing={1}>
                        <Text color={'inherit'} variant={'p2'}>
                            {currentOptionIndex + 1}/{options.length}
                        </Text>
                        <Icon kind={'dropdownArrowDown'} sx={{ fontSize: '1rem' }} />
                    </Stack>
                </Stack>
            </ProgressButton>
            <CustomMenu
                id={`${id}-menu`}
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                MenuListProps={{
                    'aria-labelledby': id,
                }}
                open={open}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                onClose={handleClose}
            >
                <Stack spacing={1}>
                    <Text variant={'p2'} weight={'medium'}>
                        {t('ui.submissions', 'Submissions')}
                    </Text>
                    <FilterControls
                        onKeyDown={(event) => {
                            // Nested selects handle their own navigation before events reach the submission menu.
                            if (!['Escape', 'Tab'].includes(event.key)) event.stopPropagation();
                        }}
                    >
                        <Stack alignItems={'center'} direction={'row'} spacing={2}>
                            <Text id={`${id}-section-label`} variant={'p3'} weight={'medium'}>
                                {t('table.section', 'Section')}
                            </Text>
                            <FilterSelect
                                inputProps={{ labelId: `${id}-section-label` }}
                                matchWidth
                                renderValue={() =>
                                    sectionOptions.find(({ id }) => id === sectionId)?.name ??
                                    t('ui.all-sections', 'All Sections')
                                }
                                displayEmpty
                                size={'small'}
                                value={sectionId}
                                onChange={(event) => selectSection(event.target.value)}
                            >
                                {[
                                    { id: '', name: t('ui.all-sections', 'All Sections'), count: totalSubmissions },
                                    ...sectionOptions,
                                ].map(({ id, name, count }) => (
                                    <SectionMenuItem key={id} value={id}>
                                        <Text sx={{ minWidth: 0 }} noWrap title={name} variant={'p3'}>
                                            {name}
                                        </Text>
                                        <Text sx={{ flexShrink: 0 }} color={'light'} variant={'p3'}>
                                            {count}
                                        </Text>
                                    </SectionMenuItem>
                                ))}
                            </FilterSelect>
                        </Stack>
                        <Stack alignItems={'center'} direction={'row'} spacing={2}>
                            <Text id={`${id}-sort-label`} sx={{ flexShrink: 0 }} variant={'p3'} weight={'medium'}>
                                {t('ui.sort', 'Sort')}
                            </Text>
                            <FilterSelect
                                inputProps={{ labelId: `${id}-sort-label` }}
                                size={'small'}
                                value={sort}
                                onChange={(event) => setSort(event.target.value)}
                            >
                                <MenuItem value={'default'}>
                                    {isGroupAssignment
                                        ? t('controls.sort-options.group-asc', 'Group (Ascending)')
                                        : t('ui.default', 'Default')}
                                </MenuItem>
                                {!isGroupAssignment && (
                                    <MenuItem value={'title-asc'}>
                                        {t('controls.sort-options.title-asc', 'Title (A-Z)')}
                                    </MenuItem>
                                )}
                                <MenuItem value={'title-desc'}>
                                    {isGroupAssignment
                                        ? t('controls.sort-options.group-desc', 'Group (Descending)')
                                        : t('controls.sort-options.title-desc', 'Title (Z-A)')}
                                </MenuItem>
                                {!isUnscored && (
                                    <MenuItem value={'grade-asc'}>
                                        {t('controls.sort-options.grade-asc', 'Grade (Ascending)')}
                                    </MenuItem>
                                )}
                                {!isUnscored && (
                                    <MenuItem value={'grade-desc'}>
                                        {t('controls.sort-options.grade-desc', 'Grade (Descending)')}
                                    </MenuItem>
                                )}
                            </FilterSelect>
                        </Stack>
                    </FilterControls>
                </Stack>
                <Divider sx={{ my: 2 }} />
                {options.map(
                    ({
                        key,
                        value,
                        number,
                        gradingStatus,
                        suggestedGrade,
                        actualGrade,
                        maxPossibleGrade,
                        section,
                        completionStatus,
                        reviewWarning,
                    }) => {
                        const isCurrentSubmission = key === currentSubmission.id;
                        const isConfirmed = gradingStatus === 'confirmed';
                        const statusPresentation =
                            STATUS_PRESENTATION[gradingStatus] ?? STATUS_PRESENTATION.not_started;

                        return (
                            <CustomMenuItem
                                key={key}
                                className={'submission-navigation-option'}
                                id={key}
                                selected={isCurrentSubmission}
                                onClick={(event) => handleSelectSubmission(event, key)}
                            >
                                <Stack sx={{ width: '100%' }} spacing={1}>
                                    <Stack sx={{ width: '100%' }} alignItems={'center'} direction={'row'} spacing={2}>
                                        <Tooltip
                                            title={t(statusPresentation.translationKey, statusPresentation.fallback)}
                                            placement={'left'}
                                        >
                                            <StatusBadge
                                                aria-label={t(
                                                    statusPresentation.translationKey,
                                                    statusPresentation.fallback
                                                )}
                                                $gradingStatus={gradingStatus}
                                                alignItems={'center'}
                                                direction={'row'}
                                                spacing={0.5}
                                            >
                                                <Text color={'inherit'} variant={'p3'}>
                                                    {number}
                                                </Text>
                                                <Icon kind={statusPresentation.icon} sx={{ fontSize: '0.75rem' }} />
                                            </StatusBadge>
                                        </Tooltip>
                                        <Text sx={{ flex: 1, minWidth: 0, pr: 1 }} noWrap title={value} variant={'p3'}>
                                            {value}
                                        </Text>
                                        {isUnscored ? (
                                            completionStatus && (
                                                <SemanticGradeDisplay snapshot={{ grade: completionStatus }} />
                                            )
                                        ) : (
                                            <Tooltip
                                                title={
                                                    isConfirmed
                                                        ? t('ui.confirmed-grade', 'Confirmed Grade')
                                                        : t('ui.suggested-grade', 'Suggested Grade')
                                                }
                                                placement={'right'}
                                            >
                                                <GradeBadge
                                                    className={'submission-grade-badge'}
                                                    $selected={isCurrentSubmission}
                                                >
                                                    <Text variant={'p3'}>
                                                        <Text
                                                            color={isConfirmed ? 'inherit' : 'primary'}
                                                            component={'span'}
                                                            variant={'p3'}
                                                        >
                                                            {actualGrade ?? suggestedGrade ?? '-'}
                                                        </Text>
                                                        /{maxPossibleGrade}
                                                    </Text>
                                                </GradeBadge>
                                            </Tooltip>
                                        )}
                                    </Stack>
                                    {(section || reviewWarning) && (
                                        <Stack sx={{ pl: 11 }} alignItems={'center'} direction={'row'} spacing={1}>
                                            <Text
                                                sx={{ flex: 1, minWidth: 0 }}
                                                color={'light'}
                                                noWrap
                                                title={section}
                                                variant={'p3'}
                                            >
                                                {section}
                                            </Text>
                                            {reviewWarning && (
                                                <WarningStack alignItems={'center'} direction={'row'} spacing={1}>
                                                    <Icon kind={'error'} />
                                                    <Text color={'inherit'} variant={'p3'}>
                                                        {t('status.review-submission', 'Review submission')}
                                                    </Text>
                                                </WarningStack>
                                            )}
                                        </Stack>
                                    )}
                                </Stack>
                            </CustomMenuItem>
                        );
                    }
                )}
            </CustomMenu>
        </Box>
    );
};

const ProgressButton = styled(ButtonBase)(({ theme }) => {
    return {
        height: '24px',
        borderRadius: '4px',
        color: theme.palette.ensightful.neutral[900],
        '&:focus-visible': {
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '2px',
        },
        '&.Mui-disabled': {
            color: theme.palette.ensightful.neutral[400],
        },
    };
});

const CustomMenu = styled(Menu)(({ theme }) => {
    return {
        '& .MuiBackdrop-root': {
            background: 'transparent',
        },
        '& .MuiPaper-root': {
            borderRadius: '16px',
            width: '445px',
            maxWidth: 'calc(100vw - 32px)',
            maxHeight: 'calc(100vh - 80px)',
            boxShadow: theme.palette.ensightful.shadows[1],
        },
        '& .MuiList-root': {
            padding: theme.spacing(2),
            gap: 0,
        },
        '& .submission-navigation-option + .submission-navigation-option': {
            marginTop: theme.spacing(1),
        },
    };
});

const CustomMenuItem = styled(MenuItem)(({ theme }) => {
    return {
        minHeight: '48px',
        padding: theme.spacing(1),
        '&.Mui-selected, &.Mui-selected:hover': {
            background: theme.palette.ensightful.primary[50],
        },
        '&:hover .submission-grade-badge, &.Mui-focusVisible .submission-grade-badge': {
            background: theme.palette.ensightful.shades[0],
        },
    };
});

const StatusBadge = styled(Stack)(({ $gradingStatus, theme }) => {
    const statusStyles = {
        not_started: {
            background: theme.palette.ensightful.neutral[700],
            color: theme.palette.ensightful.shades[0],
        },
        in_progress: {
            background: theme.palette.ensightful.warning[300],
            color: theme.palette.ensightful.neutral[900],
        },
        confirmed: {
            background: theme.palette.ensightful.success[700],
            color: theme.palette.ensightful.shades[0],
        },
    };
    const styles = statusStyles[$gradingStatus] ?? statusStyles.not_started;

    return {
        flexShrink: 0,
        borderRadius: '50px',
        minWidth: '36px',
        height: '20px',
        padding: theme.spacing(0.5, 2),
        ...styles,
    };
});

const GradeBadge = styled(Box)(({ $selected, theme }) => {
    return {
        flexShrink: 0,
        borderRadius: '40px',
        background: $selected ? theme.palette.ensightful.shades[0] : theme.palette.ensightful.primary[50],
        padding: theme.spacing(0.5, 1),
    };
});

const FilterControls = styled(Stack)(({ theme }) => ({
    flexDirection: 'row',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
}));

const FilterSelect = styled(Select)(({ theme }) => ({
    width: '158px',
    borderRadius: '40px',
    '& > fieldset': {
        border: `1px solid ${theme.palette.ensightful.neutral[500]}`,
    },
    '& > svg': {
        fontSize: '1rem',
    },
}));

const SectionMenuItem = styled(MenuItem)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(3),
    minHeight: '24px',
}));

const WarningStack = styled(Stack)(({ theme }) => ({
    flexShrink: 0,
    color: theme.palette.ensightful.error[700],
    '& svg': {
        fontSize: '1rem',
    },
}));

export default SubmissionNavigationDropdown;
